import type { HelpdeskQuote } from '../types/comercialCotacoes'
import { normalizeStatusKey } from './cotacaoFormatters'

export const DASHBOARD_STATUS_ORDER = [
  { key: 'expirada', label: 'Expirada' },
  { key: 'enviada', label: 'Enviada' },
  { key: 'aprovada', label: 'Aprovada' },
  { key: 'recusada', label: 'Recusada' },
  { key: 'em analise', label: 'Em Análise' },
  { key: 'cancelada', label: 'Cancelada' },
  { key: 'aberta', label: 'Aberta' },
  { key: 'bloqueada', label: 'Bloqueada' },
] as const

export type DashboardStatusKey = (typeof DASHBOARD_STATUS_ORDER)[number]['key']

export interface StatusDashboardMetric {
  key: DashboardStatusKey
  label: string
  count: number
  lives: number
  premium: number
  percent: number
}

export interface CotacoesDashboardMetrics {
  totalCount: number
  totalLives: number
  totalPremium: number
  byStatus: StatusDashboardMetric[]
  topByLives: HelpdeskQuote[]
  topByPremium: HelpdeskQuote[]
}

export type CotacoesVolumeGranularity = 'year' | 'month' | 'day'

/** Chaves numéricas por status no gráfico (sem colidir com key/label do ponto). */
export type CotacoesVolumeStatusDataKey =
  | Exclude<DashboardStatusKey, 'em analise'>
  | 'em_analise'

/** Chaves planas p/ recharts (sem espaços). */
export const VOLUME_STATUS_SERIES = DASHBOARD_STATUS_ORDER.map(({ key, label }) => ({
  statusKey: key,
  dataKey: (key === 'em analise' ? 'em_analise' : key) as CotacoesVolumeStatusDataKey,
  label,
})) as readonly {
  statusKey: DashboardStatusKey
  dataKey: CotacoesVolumeStatusDataKey
  label: string
}[]

export type CotacoesVolumePoint = {
  key: string
  label: string
  total: number
} & { [K in CotacoesVolumeStatusDataKey]: number }

export type CotacoesVolumeRange = {
  dataInicio?: string
  dataFim?: string
}

function emptyVolumeStatusCounts(): Record<CotacoesVolumeStatusDataKey, number> {
  const counts = {} as Record<CotacoesVolumeStatusDataKey, number>
  for (const { dataKey } of VOLUME_STATUS_SERIES) {
    counts[dataKey] = 0
  }
  return counts
}

function emptyVolumePoint(key: string, label: string): CotacoesVolumePoint {
  return {
    key,
    label,
    total: 0,
    ...emptyVolumeStatusCounts(),
  }
}

function statusToDataKey(statusKey: string): CotacoesVolumeStatusDataKey | null {
  const found = VOLUME_STATUS_SERIES.find((s) => s.statusKey === statusKey)
  return found?.dataKey ?? null
}

function getQuotePremium(quote: HelpdeskQuote): number | null {
  if (quote.total_premium == null || Number.isNaN(quote.total_premium)) return null
  return quote.total_premium
}

function inclusionTime(quote: HelpdeskQuote): number {
  if (!quote.inclusion_at) return -Infinity
  const time = new Date(quote.inclusion_at).getTime()
  return Number.isNaN(time) ? -Infinity : time
}

function compareByInclusionDesc(a: HelpdeskQuote, b: HelpdeskQuote): number {
  const diff = inclusionTime(b) - inclusionTime(a)
  if (diff !== 0) return diff
  return b.id.localeCompare(a.id)
}

/** Local calendar date parts from inclusion (same local criterion as filter T00:00:00). */
function toLocalDateParts(date: Date): { y: number; m: number; d: number } {
  return {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
  }
}

function parseFilterDateStart(isoDate: string): Date | null {
  const d = new Date(`${isoDate}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function parseFilterDateEnd(isoDate: string): Date | null {
  const d = new Date(`${isoDate}T23:59:59.999`)
  return Number.isNaN(d.getTime()) ? null : d
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function bucketKey(date: Date, granularity: CotacoesVolumeGranularity): string {
  const { y, m, d } = toLocalDateParts(date)
  const mm = String(m).padStart(2, '0')
  const dd = String(d).padStart(2, '0')
  if (granularity === 'year') return String(y)
  if (granularity === 'month') return `${y}-${mm}`
  return `${y}-${mm}-${dd}`
}

function bucketLabel(key: string, granularity: CotacoesVolumeGranularity): string {
  if (granularity === 'year') return key
  if (granularity === 'month') {
    const [ys, ms] = key.split('-')
    const monthIndex = Number(ms) - 1
    const short = new Date(Number(ys), monthIndex, 1).toLocaleDateString('pt-BR', {
      month: 'short',
      year: 'numeric',
    })
    return short.replace('.', '')
  }
  const [ys, ms, ds] = key.split('-')
  return `${ds}/${ms}/${ys}`
}

function nextBucketStart(date: Date, granularity: CotacoesVolumeGranularity): Date {
  if (granularity === 'year') {
    return new Date(date.getFullYear() + 1, 0, 1)
  }
  if (granularity === 'month') {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1)
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)
}

function alignBucketStart(date: Date, granularity: CotacoesVolumeGranularity): Date {
  if (granularity === 'year') {
    return new Date(date.getFullYear(), 0, 1)
  }
  if (granularity === 'month') {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }
  return startOfLocalDay(date)
}

function resolveRange(
  quotes: HelpdeskQuote[],
  range?: CotacoesVolumeRange
): { start: Date; end: Date } | null {
  let start: Date | null = null
  let end: Date | null = null

  if (range?.dataInicio) {
    start = parseFilterDateStart(range.dataInicio)
  }
  if (range?.dataFim) {
    end = parseFilterDateEnd(range.dataFim)
  }

  if (!start || !end) {
    let minT = Infinity
    let maxT = -Infinity
    for (const quote of quotes) {
      if (!quote.inclusion_at) continue
      const t = new Date(quote.inclusion_at).getTime()
      if (Number.isNaN(t)) continue
      if (t < minT) minT = t
      if (t > maxT) maxT = t
    }
    if (!Number.isFinite(minT) || !Number.isFinite(maxT)) return null
    if (!start) start = new Date(minT)
    if (!end) end = new Date(maxT)
  }

  if (start > end) {
    const tmp = start
    start = end
    end = tmp
  }

  return { start, end }
}

/**
 * Agrupa volume de cotações (total + contagem por status) por ano, mês ou dia.
 * A granularidade só muda a representação; o conjunto de quotes já deve estar filtrado.
 * O eixo começa no primeiro bucket com total > 0 (não no início do filtro).
 */
export function buildCotacoesVolumeSeries(
  quotes: HelpdeskQuote[],
  granularity: CotacoesVolumeGranularity,
  range?: CotacoesVolumeRange
): CotacoesVolumePoint[] {
  const bounds = resolveRange(quotes, range)
  if (!bounds) return []

  const cursor = alignBucketStart(bounds.start, granularity)
  const endAligned = alignBucketStart(bounds.end, granularity)
  const buckets = new Map<string, CotacoesVolumePoint>()

  let guard = 0
  const maxBuckets = 4000
  for (
    let d = new Date(cursor.getTime());
    d.getTime() <= endAligned.getTime() && guard < maxBuckets;
    d = nextBucketStart(d, granularity), guard += 1
  ) {
    const key = bucketKey(d, granularity)
    buckets.set(key, emptyVolumePoint(key, bucketLabel(key, granularity)))
  }

  for (const quote of quotes) {
    if (!quote.inclusion_at) continue
    const inclusion = new Date(quote.inclusion_at)
    if (Number.isNaN(inclusion.getTime())) continue
    if (inclusion < bounds.start || inclusion > bounds.end) continue

    const key = bucketKey(inclusion, granularity)
    let point = buckets.get(key)
    if (!point) {
      point = emptyVolumePoint(key, bucketLabel(key, granularity))
      buckets.set(key, point)
    }

    point.total += 1
    const dataKey = statusToDataKey(normalizeStatusKey(quote.status))
    if (dataKey) {
      point[dataKey] += 1
    }
  }

  const series = Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key))
  // Eixo X começa na primeira data/período com volume (sem buckets vazios à esquerda do filtro).
  const firstWithData = series.findIndex((point) => point.total > 0)
  if (firstWithData < 0) return []
  return series.slice(firstWithData)
}

/** Status com pelo menos 1 cotação em algum bucket — só esses entram no gráfico. */
export function getActiveVolumeStatusSeries(
  points: CotacoesVolumePoint[]
): typeof VOLUME_STATUS_SERIES {
  return VOLUME_STATUS_SERIES.filter(({ dataKey }) =>
    points.some((point) => (point[dataKey] ?? 0) > 0)
  ) as typeof VOLUME_STATUS_SERIES
}

export function buildCotacoesDashboardMetrics(quotes: HelpdeskQuote[]): CotacoesDashboardMetrics {
  const totalCount = quotes.length
  let totalPremium = 0
  let totalLives = 0

  const statusCounts = new Map<DashboardStatusKey, number>()
  const statusLives = new Map<DashboardStatusKey, number>()
  const statusPremiums = new Map<DashboardStatusKey, number>()

  for (const { key } of DASHBOARD_STATUS_ORDER) {
    statusCounts.set(key, 0)
    statusLives.set(key, 0)
    statusPremiums.set(key, 0)
  }

  for (const quote of quotes) {
    const premium = getQuotePremium(quote)
    if (premium != null) {
      totalPremium += premium
    }
    const lives =
      quote.insured_count != null && !Number.isNaN(quote.insured_count)
        ? Math.max(0, quote.insured_count)
        : 0
    totalLives += lives

    const statusKey = normalizeStatusKey(quote.status) as DashboardStatusKey
    if (statusCounts.has(statusKey)) {
      statusCounts.set(statusKey, (statusCounts.get(statusKey) ?? 0) + 1)
      statusLives.set(statusKey, (statusLives.get(statusKey) ?? 0) + lives)
      if (premium != null) {
        statusPremiums.set(statusKey, (statusPremiums.get(statusKey) ?? 0) + premium)
      }
    }
  }

  const byStatus: StatusDashboardMetric[] = DASHBOARD_STATUS_ORDER.map(({ key, label }) => {
    const count = statusCounts.get(key) ?? 0
    return {
      key,
      label,
      count,
      lives: statusLives.get(key) ?? 0,
      premium: statusPremiums.get(key) ?? 0,
      percent: totalCount > 0 ? (count / totalCount) * 100 : 0,
    }
  })

  const topByLives = [...quotes]
    .filter((q) => q.insured_count != null && !Number.isNaN(q.insured_count))
    .sort((a, b) => {
      const diff = (b.insured_count ?? 0) - (a.insured_count ?? 0)
      if (diff !== 0) return diff
      return compareByInclusionDesc(a, b)
    })
    .slice(0, 10)

  const topByPremium = [...quotes]
    .filter((q) => getQuotePremium(q) != null)
    .sort((a, b) => {
      const diff = (getQuotePremium(b) ?? 0) - (getQuotePremium(a) ?? 0)
      if (diff !== 0) return diff
      return compareByInclusionDesc(a, b)
    })
    .slice(0, 10)

  return {
    totalCount,
    totalLives,
    totalPremium,
    byStatus,
    topByLives,
    topByPremium,
  }
}
